/// <amd-module name="typeahead/core/geolocation/GeolocationDataProvider" />
/// <amd-dependency path="typeahead/core/loc" name="loc"/>
import {GeolocationClearedMessage} from "typeahead/core/geolocation/message/GeolocationClearedMessage";
import {GeolocationRequestMessage} from "typeahead/core/geolocation/message/GeolocationRequestMessage";
import {GeolocationResponseMessage} from "typeahead/core/geolocation/message/GeolocationResponseMessage";
import {IDataProvider, IDataProviderResponse} from "typeahead/core/IDataProvider";
import {ListItemType} from "typeahead/core/IListItem";
import {TypeaheadMessage} from "typeahead/core/message/TypeaheadMessage";
import {ISuggestionResult} from "typeahead/core/Suggestion";

export interface IPosition {
    coords: {
        accuracy: number;
        altitude: number;
        altitudeAccuracy: number;
        heading: number;
        latitude: number;
        longitude: number;
        speed: number;
    };
    timestamp: number;
}

export interface IPositionError {
    code: number;
    message: string;
}

export class GeolocationDataProvider implements IDataProvider {

    public static readonly CATEGORY: string = "GEOLOCATION";
    private static readonly TIMEOUT_IN_MILLISEC: number = 6000;
    private static readonly TTL_IN_MILLISEC: number = 180000;
    private static readonly MESSAGES = {
        DISPLAY_TEXT: loc.geolocationDisplayText || "Your current location",
        SUGGESTION_TEXT: loc.geolocationSuggestionText || "Use your current location",
    };

    // Postion returned by geolocation api is not a regular json object
    private static objectifyPosition(position: IPosition): IPosition {
        return {
            coords: position.coords ? {
                accuracy: position.coords.accuracy,
                altitude: position.coords.altitude,
                altitudeAccuracy: position.coords.altitudeAccuracy,
                heading: position.coords.heading,
                latitude: position.coords.latitude,
                longitude:  position.coords.longitude,
                speed: position.coords.speed,
            } : undefined,
            timestamp: position.timestamp,
        };
    }

    private static objectifyPositionError(positionError: IPositionError): IPositionError {
        return {
            code: positionError.code,
            message: positionError.message,
        };
    }

    private geolocationResult: ISuggestionResult = {
        category: GeolocationDataProvider.CATEGORY,
        listItemType: ListItemType.Suggestion,
        regionNames: {
            displayName: GeolocationDataProvider.MESSAGES.SUGGESTION_TEXT,
            lastSearchName: GeolocationDataProvider.MESSAGES.DISPLAY_TEXT,
        },
        type: GeolocationDataProvider.CATEGORY,
    } as ISuggestionResult;

    public fetchData(options: any): JQueryPromise<IDataProviderResponse> {
        const deferred: JQueryDeferred<IDataProviderResponse> = $.Deferred();
        return deferred.resolve({results: [this.getGeolocationResult()]}).promise();
    }

    public findGeolocation(domElementId: string, submitButton?: JQuery): void {
        let responseReceived = false;
        TypeaheadMessage.publish(new GeolocationRequestMessage(domElementId));
        navigator.geolocation.getCurrentPosition((position: IPosition) => {
                responseReceived = true;
                this.setGeolocation(position);
                TypeaheadMessage.publish(new GeolocationResponseMessage(domElementId,
                    GeolocationDataProvider.objectifyPosition(position), undefined, this.getGeolocationResult(),
                    submitButton));
                setTimeout(() => {
                    this.setGeolocation(undefined);
                    TypeaheadMessage.publish(new GeolocationClearedMessage(domElementId, this.getGeolocationResult()));
                }, GeolocationDataProvider.TTL_IN_MILLISEC); // clear after 3 minutes
            },
            // Multiple error responses can be received for a single geolocation request
            (positionError: IPositionError) => {
                if (!responseReceived) {
                    responseReceived = true;
                    TypeaheadMessage.publish(new GeolocationResponseMessage(domElementId, undefined,
                        GeolocationDataProvider.objectifyPositionError(positionError), this.getGeolocationResult()));
                }
            },
            {
                enableHighAccuracy: true,
                maximumAge: 0,
                timeout: GeolocationDataProvider.TIMEOUT_IN_MILLISEC,
            });

        // Fallback timeout in case gelocation api never responds back
        setTimeout(() => {
            if (!responseReceived) {
                responseReceived = true;
                TypeaheadMessage.publish(new GeolocationResponseMessage(domElementId, undefined,
                    {code: 3, message: "Fallback timeout expired"} as IPositionError, this.getGeolocationResult()));
            }
        }, GeolocationDataProvider.TIMEOUT_IN_MILLISEC + 15000);
    }

    private getGeolocationResult(): ISuggestionResult {
        return this.geolocationResult;
    }

    private setGeolocation(position: IPosition): void {
        if (position && position.coords) {
           this.geolocationResult.coordinates = {
               lat: position.coords.latitude + "",
               long: position.coords.longitude + "",
           };
        } else {
            this.geolocationResult.coordinates = undefined;
        }
    }
}
